/**
 * Restock urgency tiers rule inspect view per scope §6.6.4. Atlas Day 3a P6.
 *
 * D-2 lock 2026-05-27 applied: RPC-default substrate model (multipliers
 * consumed via COALESCE-fallback in forecast_restock + siblings; defaults
 * 1.0 + 1.5; NOT DB-stored at current substrate state). Surface line +
 * L4/L7/L8 read RPC-default location honestly per scope-substrate
 * verification discipline.
 *
 * BACKLOG candidate filed for Day 3b sweep: restock urgency multipliers
 * as DB-stored params (configurability-substrate gap; Settings → Engine
 * Tuning subsection per Day 86 A6 pattern; post-launch).
 */

import type { JSX } from 'react';
import { URL_SETTINGS_PARAMS_ENGINE_TUNING } from '../../../lib/atlas/atlasUrls';
import { RESTOCK_URGENCY_TIERS_PROPAGATION } from '../../../lib/atlas/propagationMaps';
import InspectShell from './InspectShell';
import LayerSection from './LayerSection';
import ComposabilityReservedLayer from './ComposabilityReservedLayer';

export default function RestockUrgencyTiersInspect(): JSX.Element {
  return (
    <InspectShell
      subRegionName="Restock urgency tiers"
      surfaceLine="ISG flags inventory for restock below tightness thresholds. Red = 1.0× lead time, Yellow = 1.5× lead time (RPC-default fallbacks; not currently DB-configurable). Tighter = more aggressive replenishment posture."
    >
      <LayerSection layer={4} title="Math vs judgment">
        <p>
          Per scope §6.6.4 L4 + D-2 lock 2026-05-27: thresholds are ISG&apos;s choices
          (judgment) but currently encoded as RPC-default COALESCE-fallback values not data
          overrides. RPC defaults: Red multiplier = 1.0, Yellow multiplier = 1.5.
        </p>
        <p>
          Tightness arithmetic: <code>days_to_stockout / supplier_lead_time_days</code> ratio;
          compared against multiplier thresholds. Red when ratio &lt; 1.0; Yellow when ratio
          &lt; 1.5; Healthy otherwise.
        </p>
      </LayerSection>

      <LayerSection layer={5} title="Mechanical vs strategic">
        <p>
          Strategic at threshold-multiplier altitude per scope §6.6.4 L5 — multipliers express
          ISG&apos;s replenishment-aggression posture. Mechanical at arithmetic altitude (ratio
          comparison is structural code).
        </p>
      </LayerSection>

      <LayerSection layer={6} title="Operational vs ownership">
        <p>
          Ownership-altitude per scope §6.6.4 L6. Threshold multipliers define ISG&apos;s
          replenishment stance. Currently encoded as RPC-default (code-level), so layer-6
          per-value applicability is constrained today — per-distributor override capability
          requires substrate migration (BACKLOG candidate per D-2 lock).
        </p>
      </LayerSection>

      <LayerSection layer={7} title="Trace">
        <p>
          Per D-2 lock 2026-05-27: <strong>untraced-by-design</strong> at current substrate
          state — RPC-default multipliers aren&apos;t DB-stored, so no per-row trace events.
          Trace surfaces via git history of <code>forecast_restock</code> RPC body when
          defaults change. Future per-distributor override capability would carry{' '}
          <code>params.provenance</code> trace.
        </p>
        <p>
          RPC defaults verified Phase 0 #10:{' '}
          <code>supplemental_rpcs_path_config.sql:587-588</code> +{' '}
          <code>day75_dropship_fulfillment_path_activation.sql:1203-1204</code> +{' '}
          <code>forecast_restock.sql:7-8</code> COALESCE-fallback to 1.0 + 1.5.
        </p>
      </LayerSection>

      <LayerSection layer={8} title="Propagation">
        <p>Curated propagation map (verified as of 2026-05-27 per scope §8 staleness marker):</p>
        <ul className="list-disc pl-5 text-xs space-y-2">
          {RESTOCK_URGENCY_TIERS_PROPAGATION.map((entry) => (
            <li key={entry.field}>
              <strong>
                <code>{entry.field}</code>
              </strong>{' '}
              edits affect:
              <ul className="list-circle pl-5 mt-1 space-y-0.5">
                {entry.affects.map((eff) => (
                  <li key={eff}>{eff}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={9} title="Reversibility">
        <p>
          Reversible-with-residue per scope §6.6.4 L9. Multiplier changes affect future
          forecast; past <code>forecast_restock</code> outputs preserve prior-tier state.
          Note: current RPC-default substrate model means changes require code-level edit (not
          configuration-altitude data edit) — layer 9 framing reflects code-level reversibility
          per code-change-via-deployment pattern.
        </p>
      </LayerSection>

      <ComposabilityReservedLayer />

      <LayerSection layer={11} title="Positional contribution">
        <p>
          Substantive-contribution per scope §6.6.4 L11. Surfaces in title block sentence 3:
          &quot;flags inventory for restock tiered by projected time-to-stockout.&quot;
        </p>
      </LayerSection>

      <LayerSection layer={4 /* cross-links footer */} title="Cross-links">
        <p>
          Per scope §6.6.4 Cross-link + D5 lock (Atlas Day 2 P5): Settings → Engine Tuning
          subsection within Params &amp; Defaults parent anchor.
        </p>
        <ul className="list-disc pl-5 text-xs">
          <li>
            <a href={URL_SETTINGS_PARAMS_ENGINE_TUNING} className="text-accent hover:underline">
              Settings → Params &amp; Defaults (Engine Tuning subsection)
            </a>
          </li>
        </ul>
      </LayerSection>
    </InspectShell>
  );
}
